import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Pencil, Search, Trash2 } from "lucide-react";
import { api, pct, thb, TIER_LABELS, type Tier } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/toast";
import { BrandHeading } from "@/components/BrandFrame";

const PAGE_SIZE = 100;
const TIERS: Tier[] = ["license", "wholesale", "general"];

export default function AdminCatalogPage() {
  const qc = useQueryClient();
  const { push } = useToast();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q.trim()), 220); return () => clearTimeout(t); }, [q]);

  const inf = useInfiniteQuery({
    queryKey: ["adminProducts", debouncedQ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api.products({ q: debouncedQ, page: pageParam, pageSize: PAGE_SIZE, includeDeleted: 1 }),
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
  });
  const rows = useMemo(() => inf.data?.pages.flatMap((p) => p.rows) ?? [], [inf.data]);
  const total = inf.data?.pages[0]?.total ?? 0;

  const parentRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({
    count: rows.length + (inf.hasNextPage ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 8,
  });
  useEffect(() => {
    const last = v.getVirtualItems().at(-1);
    if (last && last.index >= rows.length - 5 && inf.hasNextPage && !inf.isFetchingNextPage) inf.fetchNextPage();
  }, [v.getVirtualItems(), rows.length, inf.hasNextPage, inf.isFetchingNextPage]);

  const [editing, setEditing] = useState<any | null>(null);
  const save = useMutation({
    mutationFn: (b: any) => api.patchProduct(b.partnumber, b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminProducts"] }); qc.invalidateQueries({ queryKey: ["products"] }); setEditing(null); push({ title: "บันทึกแล้ว" }); },
    onError: (e: any) => push({ title: "บันทึกไม่สำเร็จ", description: e.message, variant: "error" }),
  });
  const del = useMutation({
    mutationFn: (partnumber: string) => api.deleteProduct(partnumber),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminProducts"] }); push({ title: "ซ่อนรายการแล้ว" }); },
    onError: (e: any) => push({ title: "ลบไม่สำเร็จ", description: e.message, variant: "error" }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <BrandHeading>แก้ไขแค็ตตาล็อก</BrandHeading>
        <span className="text-sm text-muted-foreground">{total.toLocaleString()} รายการ</span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="h-11 pl-10" placeholder="ค้นหารหัสสินค้า, ชื่อรายการ, BC365…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div ref={parentRef} className="h-[calc(100vh-280px)] overflow-auto rounded-lg border border-border bg-card">
        <div className="sticky top-0 z-10 grid grid-cols-[170px_minmax(280px,1fr)_120px_120px_repeat(3,minmax(140px,200px))_80px] items-center gap-3 border-b border-border bg-muted/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>รหัสสินค้า</span><span>ชื่อรายการ</span><span>แบรนด์</span><span className="text-right">ราคาตั้ง</span>
          {TIERS.map((t) => <span key={t} className="text-right">{TIER_LABELS[t]}</span>)}
          <span></span>
        </div>
        <div style={{ height: v.getTotalSize(), position: "relative" }}>
          {v.getVirtualItems().map((vi) => {
            const row = rows[vi.index];
            if (!row) return <div key={vi.key} className="absolute left-0 top-0 w-full px-4 py-3 text-xs text-muted-foreground" style={{ transform: `translateY(${vi.start}px)`, height: vi.size }}>กำลังโหลด…</div>;
            const isDeleted = !!row.deleted_at;
            return (
              <div key={vi.key} className="absolute left-0 top-0 w-full" style={{ transform: `translateY(${vi.start}px)`, height: vi.size }}>
                <div className={`grid grid-cols-[170px_minmax(280px,1fr)_120px_120px_repeat(3,minmax(140px,200px))_80px] items-center gap-3 border-b border-border/40 px-4 py-2 text-sm transition-colors duration-300 hover:bg-secondary/40 ${isDeleted ? "opacity-50" : ""}`}>
                  <span className="flex flex-col leading-tight min-w-0">
                    <span className="font-mono text-xs font-semibold">{row.partnumber}{isDeleted && <Badge className="ml-2" variant="net">ลบแล้ว</Badge>}</span>
                    {row.bc365_code && <span className="font-mono text-[10px] text-muted-foreground">{row.bc365_code}</span>}
                  </span>
                  <span className="truncate">{row.description ?? "—"}</span>
                  <span className="text-xs text-muted-foreground">{row.brand1}</span>
                  <span className="text-right font-mono text-xs">{thb(row.sale_price)}</span>
                  {TIERS.map((t) => {
                    const isNet = row[`${t}_is_net`] === 1;
                    return (
                      <span key={t} className="flex flex-col items-end leading-tight">
                        <span className="font-mono text-sm font-semibold">{thb(row[`${t}_unit_price`])}</span>
                        <span className="text-[10px] text-muted-foreground">{isNet ? "NET" : `−${pct(row[`${t}_disc`])}`}</span>
                      </span>
                    );
                  })}
                  <span className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(row)}><Pencil className="h-3.5 w-3.5" /></Button>
                    {!isDeleted && (
                      <Button variant="ghost" size="sm" onClick={() => confirm(`ลบ ${row.partnumber}?`) && del.mutate(row.partnumber)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && <EditProductDialog row={editing} onClose={() => setEditing(null)} onSave={(p) => save.mutate(p)} saving={save.isPending} />}
      </Dialog>
    </div>
  );
}

function EditProductDialog({ row, onSave, saving }: { row: any; onClose: () => void; onSave: (p: any) => void; saving: boolean }) {
  const [draft, setDraft] = useState({ ...row });
  function set<K extends string>(k: K, v: any) { setDraft((d: any) => ({ ...d, [k]: v })); }
  function num(v: string): number | null { if (v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-mono">{row.partnumber}</DialogTitle>
      </DialogHeader>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label>ชื่อรายการ</Label>
          <Input value={draft.description ?? ""} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div>
          <Label>ราคาตั้ง</Label>
          <Input value={draft.sale_price ?? ""} onChange={(e) => set("sale_price", num(e.target.value))} />
        </div>
        <div>
          <Label>สต๊อก</Label>
          <Input value={draft.stock ?? ""} onChange={(e) => set("stock", num(e.target.value))} />
        </div>
        {(["license", "wholesale", "general"] as Tier[]).map((t) => {
          const isNet = draft[`${t}_is_net`] === 1;
          return (
            <div key={t} className="rounded-md border border-border p-3 sm:col-span-2">
              <div className="mb-2 flex items-center justify-between">
                <Badge variant={t}>{TIER_LABELS[t]}</Badge>
                <label className="inline-flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={isNet}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      set(`${t}_is_net`, checked ? 1 : 0);
                      if (checked) set(`${t}_disc`, null);
                    }}
                  />
                  NET (ไม่มีส่วนลด)
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>ส่วนลด (0–1)</Label>
                  <Input
                    disabled={isNet}
                    value={draft[`${t}_disc`] ?? ""}
                    onChange={(e) => {
                      const v = num(e.target.value);
                      set(`${t}_disc`, v);
                      const sp = Number(draft.sale_price);
                      if (v != null && Number.isFinite(sp)) set(`${t}_unit_price`, Math.round(sp * (1 - v) * 100) / 100);
                    }}
                  />
                </div>
                <div>
                  <Label>ราคาสุทธิ</Label>
                  <Input value={draft[`${t}_unit_price`] ?? ""} onChange={(e) => set(`${t}_unit_price`, num(e.target.value))} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <DialogFooter>
        <Button variant="primary" onClick={() => onSave(draft)} disabled={saving}>{saving ? "กำลังบันทึก…" : "บันทึก"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
